import React, {useState, useEffect, useMemo, useCallback} from 'react';
import {View, StyleSheet, ScrollView} from 'react-native';
import {
  Portal, Dialog, Text, Button, IconButton, Menu,
  TextInput, Switch, ActivityIndicator, Snackbar,
} from 'react-native-paper';
import {colors, spacing} from '../../theme';
import {orderApi} from '../../services/api/orderApi';
import {useConfigStore} from '../../store/useConfigStore';

const DEFAULT_PAYMENT_MODES = [
  {label: 'Cash', value: 'CASH'},
  {label: 'Bank Transfer', value: 'BANK_TRANSFER'},
  {label: 'Card Swipe', value: 'CARD_SWIPE'},
  {label: 'UPI', value: 'UPI'},
  {label: 'Other', value: 'OTHER'},
];

const ReceivePaymentModal = ({visible, onDismiss, order, onPaymentComplete}) => {
  const {getProperty, fetchByType} = useConfigStore();

  const PAYMENT_MODES = useMemo(() => {
    const configured = getProperty('PAYMENT', 'PAYMENT_MODES');
    if (configured) {
      return configured.split(',').map(m => m.trim()).filter(Boolean).map(m => ({
        label: m,
        value: m.toUpperCase().replace(/\s+/g, '_'),
      }));
    }
    return DEFAULT_PAYMENT_MODES;
  }, [getProperty]);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [snackbar, setSnackbar] = useState({visible: false, message: ''});

  const balanceDue = paymentData?.balanceDue ?? order?.balanceAmount ?? 0;

  const [paymentRows, setPaymentRows] = useState([
    {paymentMode: 'CASH', referenceNumber: '', amount: String(balanceDue), menuVisible: false},
  ]);
  const [adjustToZero, setAdjustToZero] = useState(false);
  const [addToCredits, setAddToCredits] = useState(false);

  // Fetch existing payments and config when modal opens
  useEffect(() => {
    if (visible) {
      fetchByType('PAYMENT');
    }
  }, [visible, fetchByType]);

  useEffect(() => {
    if (visible && order?.id) {
      setLoading(true);
      orderApi.getPayments(order.id)
        .then(res => {
          const data = res.data?.data;
          setPaymentData(data);
          const bal = data?.balanceDue ?? order.balanceAmount ?? 0;
          setPaymentRows([
            {paymentMode: 'CASH', referenceNumber: '', amount: String(bal), menuVisible: false},
          ]);
          setAdjustToZero(false);
          setAddToCredits(false);
        })
        .catch(err => {
          setSnackbar({visible: true, message: err.response?.data?.message || 'Failed to load payments'});
        })
        .finally(() => setLoading(false));
    }
  }, [visible, order?.id, order?.balanceAmount]);

  const currentBalance = useMemo(() => {
    const bal = Number(paymentData?.balanceDue ?? order?.balanceAmount ?? 0);
    const paid = paymentRows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
    return bal - paid;
  }, [paymentData, order, paymentRows]);

  const roundingAdjustment = useMemo(() => {
    if (!adjustToZero) return 0;
    return currentBalance > 0 ? currentBalance : 0;
  }, [adjustToZero, currentBalance]);

  const finalBalance = useMemo(() => {
    return currentBalance - roundingAdjustment;
  }, [currentBalance, roundingAdjustment]);

  const totalBeingPaid = useMemo(() => {
    return paymentRows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0) + roundingAdjustment;
  }, [paymentRows, roundingAdjustment]);

  const showAddToCredits = finalBalance < 0;

  const addRow = useCallback(() => {
    setPaymentRows(prev => [...prev, {paymentMode: 'CASH', referenceNumber: '', amount: '', menuVisible: false}]);
  }, []);

  const removeRow = useCallback(index => {
    setPaymentRows(prev => prev.filter((_, i) => i !== index));
  }, []);

  const updateRow = useCallback((index, field, value) => {
    setPaymentRows(prev => prev.map((r, i) => i === index ? {...r, [field]: value} : r));
  }, []);

  const handleSubmit = async () => {
    const payments = paymentRows
      .filter(r => Number(r.amount) > 0)
      .map(r => ({
        amount: Number(r.amount),
        paymentMode: r.paymentMode,
        referenceNumber: r.referenceNumber || null,
      }));

    if (payments.length === 0) {
      setSnackbar({visible: true, message: 'Enter at least one payment amount'});
      return;
    }

    setSubmitting(true);
    try {
      await orderApi.createPayment(order.id, {
        payments,
        roundingAdjustment: roundingAdjustment > 0 ? roundingAdjustment : null,
        addToCredits: showAddToCredits ? addToCredits : false,
      });
      onPaymentComplete?.();
      onDismiss();
    } catch (error) {
      setSnackbar({visible: true, message: error.response?.data?.message || 'Payment failed'});
    } finally {
      setSubmitting(false);
    }
  };

  const pastPayments = paymentData?.payments || [];
  const hasPastPayments = pastPayments.length > 0;

  const getModeLabel = value => PAYMENT_MODES.find(m => m.value === value)?.label || value;

  return (
    <Portal>
      <Dialog visible={visible} onDismiss={onDismiss} style={styles.dialog}>
        <View style={styles.titleRow}>
          <Text style={styles.title}>Payments</Text>
          <IconButton icon="close" size={20} onPress={onDismiss} />
        </View>
        <Dialog.Content>
          {loading ? (
            <ActivityIndicator size="small" color={colors.primary} style={{marginVertical: spacing.xl}} />
          ) : (
            <ScrollView style={styles.scrollArea} showsVerticalScrollIndicator={false}>
              {/* Summary */}
              <View style={styles.summarySection}>
                <SummaryRow label="Total" value={
                  paymentData
                    ? Number(paymentData.orderTotal || 0) - Number(paymentData.discountAmount || 0) + Number(paymentData.taxAmount || 0)
                    : Number(order?.totalAmount || 0) - Number(order?.discountAmount || 0) + Number(order?.taxAmount || 0)
                } />
                {hasPastPayments && (
                  <>
                    <SummaryRow label="Paid amount" value={paymentData?.totalPaid} color={colors.success} />
                    <SummaryRow label="Payment due" value={paymentData?.balanceDue} color={colors.error} />
                  </>
                )}
              </View>

              {/* Past Payments Table */}
              {hasPastPayments && (
                <View style={styles.pastPaymentsSection}>
                  <Text style={styles.subTitle}>Payment History</Text>
                  <View style={styles.tableHeader}>
                    <Text style={[styles.headerText, styles.modeCol]}>Mode</Text>
                    <Text style={[styles.headerText, styles.refCol]}>Ref#</Text>
                    <Text style={[styles.headerText, styles.amtCol]}>Amount</Text>
                  </View>
                  {pastPayments.map(p => (
                    <View key={p.id} style={styles.tableRow}>
                      <View style={[styles.modeCol, {flexDirection: 'row', alignItems: 'center', gap: 4}]}>
                        <Text style={styles.cellText}>{getModeLabel(p.paymentMode)}</Text>
                        {p.sourceType === 'BULK_PAYMENT' && (
                          <View style={styles.bulkTag}>
                            <Text style={styles.bulkTagText}>Bulk</Text>
                          </View>
                        )}
                      </View>
                      <Text style={[styles.cellText, styles.refCol]}>{p.referenceNumber || '-'}</Text>
                      <Text style={[styles.cellText, styles.amtCol]}>{'\u20B9'}{Number(p.amount).toLocaleString()}</Text>
                    </View>
                  ))}
                </View>
              )}

              {/* New Payment Rows */}
              <Text style={[styles.subTitle, {marginTop: spacing.md}]}>New Payment</Text>
              {paymentRows.map((row, index) => (
                <View key={index} style={styles.paymentRow}>
                  <Menu
                    visible={row.menuVisible}
                    onDismiss={() => updateRow(index, 'menuVisible', false)}
                    anchor={
                      <Button
                        mode="outlined"
                        compact
                        onPress={() => updateRow(index, 'menuVisible', true)}
                        style={styles.modeButton}
                        labelStyle={styles.modeButtonLabel}>
                        {getModeLabel(row.paymentMode)}
                      </Button>
                    }>
                    {PAYMENT_MODES.map(mode => (
                      <Menu.Item
                        key={mode.value}
                        title={mode.label}
                        onPress={() => {
                          updateRow(index, 'paymentMode', mode.value);
                          updateRow(index, 'menuVisible', false);
                        }}
                      />
                    ))}
                  </Menu>
                  <TextInput
                    mode="outlined"
                    placeholder="Txn #"
                    value={row.referenceNumber}
                    onChangeText={v => updateRow(index, 'referenceNumber', v)}
                    style={styles.refInput}
                    dense
                  />
                  <TextInput
                    mode="outlined"
                    placeholder="Amount"
                    value={row.amount}
                    onChangeText={v => updateRow(index, 'amount', v)}
                    keyboardType="numeric"
                    style={styles.amountInput}
                    dense
                  />
                  {paymentRows.length > 1 && (
                    <IconButton icon="close-circle-outline" size={18} iconColor={colors.error}
                      onPress={() => removeRow(index)} style={{margin: 0}} />
                  )}
                </View>
              ))}

              <Button
                mode="text"
                icon="plus"
                compact
                onPress={addRow}
                style={styles.addButton}
                labelStyle={{fontSize: 13}}>
                Add Payment
              </Button>

              {/* Adjust to zero switch */}
              <View style={styles.switchRow}>
                <Text style={styles.switchLabel}>Adjust balance to zero?</Text>
                <Switch
                  value={adjustToZero}
                  onValueChange={setAdjustToZero}
                  color={colors.primary}
                />
              </View>
              {adjustToZero && roundingAdjustment > 0 && (
                <Text style={styles.adjustText}>
                  Rounding adjustment: {'\u20B9'}{roundingAdjustment.toLocaleString()}
                </Text>
              )}

              {/* Add to credits */}
              {showAddToCredits && (
                <View style={styles.switchRow}>
                  <Text style={styles.switchLabel}>Add excess to customer credits?</Text>
                  <Switch
                    value={addToCredits}
                    onValueChange={setAddToCredits}
                    color={colors.primary}
                  />
                </View>
              )}

              {/* Live Balance */}
              <View style={styles.balanceRow}>
                <Text style={styles.balanceLabel}>Remaining Balance:</Text>
                <Text style={[styles.balanceValue, {color: finalBalance <= 0 ? colors.success : colors.error}]}>
                  {'\u20B9'}{Math.abs(finalBalance).toLocaleString()}
                  {finalBalance < 0 ? ' (overpaid)' : ''}
                </Text>
              </View>
            </ScrollView>
          )}
        </Dialog.Content>
        <Dialog.Actions>
          <Button onPress={onDismiss} textColor={colors.textSecondary}>Cancel</Button>
          <Button
            mode="contained"
            onPress={handleSubmit}
            loading={submitting}
            disabled={submitting || loading}
            style={styles.addBtn}>
            ADD
          </Button>
        </Dialog.Actions>
      </Dialog>
      <Snackbar
        visible={snackbar.visible}
        onDismiss={() => setSnackbar({visible: false, message: ''})}
        duration={3000}>
        {snackbar.message}
      </Snackbar>
    </Portal>
  );
};

const SummaryRow = ({label, value, color}) => (
  <View style={styles.summaryRow}>
    <Text style={[styles.summaryLabel, color && {color}]}>{label}</Text>
    <Text style={[styles.summaryValue, color && {color}]}>
      {'\u20B9'}{Number(value || 0).toLocaleString()}
    </Text>
  </View>
);

const styles = StyleSheet.create({
  dialog: {maxHeight: '85%', borderRadius: 15},
  titleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: spacing.lg,
    paddingRight: spacing.xs,
  },
  title: {fontSize: 18, fontWeight: '700', color: colors.text},
  scrollArea: {maxHeight: 450},
  summarySection: {marginBottom: spacing.md},
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: spacing.xs,
  },
  summaryLabel: {fontSize: 14, color: colors.text},
  summaryValue: {fontSize: 14, fontWeight: '600', color: colors.text},
  subTitle: {fontSize: 14, fontWeight: '700', color: colors.text, marginBottom: spacing.sm},
  pastPaymentsSection: {marginBottom: spacing.md},
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: colors.textSecondary,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.xs,
    borderRadius: 4,
  },
  headerText: {fontSize: 11, fontWeight: '700', color: colors.white},
  modeCol: {flex: 2},
  refCol: {flex: 2},
  amtCol: {flex: 1.5, textAlign: 'right'},
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 4,
    paddingHorizontal: spacing.xs,
    borderBottomWidth: 1,
    borderBottomColor: colors.divider,
  },
  cellText: {fontSize: 12, color: colors.text},
  paymentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.sm,
    gap: 4,
  },
  modeButton: {borderColor: colors.border, minWidth: 80},
  modeButtonLabel: {fontSize: 11},
  refInput: {flex: 1, fontSize: 13, height: 36},
  amountInput: {width: 80, fontSize: 13, height: 36},
  addButton: {alignSelf: 'flex-start', marginBottom: spacing.sm},
  switchRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  switchLabel: {fontSize: 13, color: colors.text, flex: 1},
  adjustText: {fontSize: 12, color: colors.textSecondary, marginBottom: spacing.sm, marginLeft: spacing.xs},
  balanceRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    marginTop: spacing.sm,
  },
  balanceLabel: {fontSize: 15, fontWeight: '700', color: colors.text},
  balanceValue: {fontSize: 16, fontWeight: '700'},
  addBtn: {backgroundColor: colors.primary, borderRadius: 10, paddingHorizontal: 10, width: 80, height: 36},
  bulkTag: {
    backgroundColor: colors.info + '20',
    paddingHorizontal: 4,
    paddingVertical: 1,
    borderRadius: 3,
  },
  bulkTagText: {fontSize: 9, fontWeight: '700', color: colors.info},
});

export default ReceivePaymentModal;
